import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';

import {
  CameraAlt as CameraIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  WineBar as WineIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { learnWineFromImages, WineAnalysis } from '../services/api';

const WineLearning: React.FC = () => {
  const { user } = useAuth();
  const [frontImage, setFrontImage] = useState<string | null>(null);
  const [backImage, setBackImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [analysis, setAnalysis] = useState<WineAnalysis | null>(null);
  const [createdItem, setCreatedItem] = useState<any>(null);
  
  const frontImageRef = useRef<HTMLInputElement>(null);
  const backImageRef = useRef<HTMLInputElement>(null);

  const handleImageCapture = (type: 'front' | 'back') => {
    const inputRef = type === 'front' ? frontImageRef : backImageRef;
    inputRef.current?.click();
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>, type: 'front' | 'back') => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      const base64 = result.split(',')[1]; // Remove data URL prefix
      
      if (type === 'front') {
        setFrontImage(base64);
      } else {
        setBackImage(base64);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLearnWine = async () => {
    if (!frontImage) {
      setError('Front image is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setAnalysis(null);
    setCreatedItem(null);

    try {
      const response = await learnWineFromImages(frontImage, backImage || undefined);
      const data = response.data;

      if (data.success && data.data) {
        setAnalysis(data.data.analysis);
        setCreatedItem(data.data.item);
        setSuccess('Wine learned successfully!');
      } else {
        setError(data.error || data.message || 'Failed to learn wine');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to learn wine');
    } finally {
      setLoading(false);
    }
  };

  const handleClearImages = () => {
    setFrontImage(null);
    setBackImage(null);
    setAnalysis(null);
    setCreatedItem(null);
    setError('');
    setSuccess('');
    if (frontImageRef.current) frontImageRef.current.value = '';
    if (backImageRef.current) backImageRef.current.value = '';
  };

  const renderImagePreview = (image: string | null, type: 'front' | 'back') => (
    <Box
      sx={{
        width: '100%',
        height: 200,
        border: '2px dashed #ccc',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f5f5f5',
      }}
    >
      {image ? (
        <>
                        <img
                src={`data:image/jpeg;base64,${image}`}
                alt={`${type} of bottle`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
          <IconButton
            onClick={() => type === 'front' ? setFrontImage(null) : setBackImage(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ) : (
        <Box textAlign="center">
          <CameraIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
          <Typography variant="body2" color="textSecondary">
            {type === 'front' ? 'Front of bottle' : 'Back of bottle (optional)'}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderAnalysisCard = () => {
    if (!analysis) return null;

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Wine Analysis Results
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">Name</Typography>
              <Typography variant="body1" gutterBottom>{analysis.name}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary">Winery</Typography>
              <Typography variant="body1" gutterBottom>{analysis.winery}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary">Vintage</Typography>
              <Typography variant="body1" gutterBottom>{analysis.vintage || 'Not specified'}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary">Country</Typography>
              <Typography variant="body1" gutterBottom>{analysis.country}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary">Region</Typography>
              <Typography variant="body1" gutterBottom>{analysis.region || 'Not specified'}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">Grapes</Typography>
              <Box sx={{ mb: 2 }}>
                {analysis.grapes.map((grape, index) => (
                  <Chip key={index} label={grape} size="small" sx={{ mr: 1, mb: 1 }} />
                ))}
              </Box>
              
              <Typography variant="subtitle2" color="textSecondary">Alcohol Content</Typography>
              <Typography variant="body1" gutterBottom>{analysis.alcohol_content}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary">Average Price</Typography>
              <Typography variant="body1" gutterBottom>{analysis.average_price}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary">Rating</Typography>
              <Typography variant="body1" gutterBottom>{analysis.average_rating}</Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="textSecondary">Tasting Notes</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {analysis.tasting_notes}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const renderCreatedItemCard = () => {
    if (!createdItem) return null;

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Created Item
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">Code</Typography>
              <Typography variant="body1" gutterBottom>{createdItem.code}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary">Name</Typography>
              <Typography variant="body1" gutterBottom>{createdItem.name}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary">Price</Typography>
              <Typography variant="body1" gutterBottom>€{createdItem.price}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">Cost</Typography>
              <Typography variant="body1" gutterBottom>€{createdItem.cost}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary">On Hand</Typography>
              <Typography variant="body1" gutterBottom>{createdItem.on_hand || 0}</Typography>
              
              <Typography variant="subtitle2" color="textSecondary">Tags</Typography>
              <Typography variant="body1" gutterBottom>{createdItem.tags || 'None'}</Typography>
            </Box>
          </Box>
          <Typography variant="subtitle2" color="textSecondary">Description</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {createdItem.description}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  if (!user?.is_admin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <WineIcon sx={{ mr: 2, fontSize: 32 }} />
        <Typography variant="h4">Wine Learning</Typography>
      </Box>

      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Take photos of wine bottles to automatically learn their details using AI analysis.
        The front image is required, and the back image is optional but can provide additional information.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Front of Bottle *
              </Typography>
              {renderImagePreview(frontImage, 'front')}
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => handleImageCapture('front')}
                  fullWidth
                >
                  Upload Image
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Back of Bottle (Optional)
              </Typography>
              {renderImagePreview(backImage, 'back')}
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => handleImageCapture('back')}
                  fullWidth
                >
                  Upload Image
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <WineIcon />}
          onClick={handleLearnWine}
          disabled={!frontImage || loading}
          size="large"
        >
          {loading ? 'Learning Wine...' : 'Learn Wine'}
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleClearImages}
          disabled={loading}
        >
          Clear Images
        </Button>
      </Box>

      {renderAnalysisCard()}
      {renderCreatedItemCard()}

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={frontImageRef}
        accept="image/*"
        onChange={(e) => handleImageUpload(e, 'front')}
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={backImageRef}
        accept="image/*"
        onChange={(e) => handleImageUpload(e, 'back')}
        style={{ display: 'none' }}
      />
    </Box>
  );
};

export default WineLearning;
